import * as React from "react"

export type ThemeMode = "system" | "light" | "dark"

interface ThemeContextValue {
  mode: ThemeMode
  setMode: (mode: ThemeMode) => void
  isDark: boolean
  toggleTheme: () => void
}

const ThemeContext = React.createContext<ThemeContextValue | null>(null)

function readStoredMode(): ThemeMode {
  if (typeof window === "undefined") return "system"
  const stored = window.localStorage.getItem("theme")
  if (stored === "light" || stored === "dark" || stored === "system") return stored
  return "system"
}

function prefersDark(): boolean {
  if (typeof window === "undefined" || !window.matchMedia) return false
  return window.matchMedia("(prefers-color-scheme: dark)").matches
}

function resolveIsDark(mode: ThemeMode): boolean {
  if (mode === "dark") return true
  if (mode === "light") return false
  return prefersDark()
}

export function ThemeProvider({ children }: { children: React.ReactNode }) {
  const [mode, setModeState] = React.useState<ThemeMode>(() => readStoredMode())
  const [isDark, setIsDark] = React.useState<boolean>(() => resolveIsDark(readStoredMode()))

  React.useEffect(() => {
    window.localStorage.setItem("theme", mode)
    setIsDark(resolveIsDark(mode))
  }, [mode])

  React.useEffect(() => {
    const root = document.documentElement
    if (isDark) root.classList.add("dark")
    else root.classList.remove("dark")
  }, [isDark])

  React.useEffect(() => {
    if (mode !== "system" || typeof window === "undefined" || !window.matchMedia) return
    const mq = window.matchMedia("(prefers-color-scheme: dark)")
    const handler = (e: MediaQueryListEvent) => setIsDark(e.matches)
    mq.addEventListener("change", handler)
    return () => mq.removeEventListener("change", handler)
  }, [mode])

  const setMode = React.useCallback((next: ThemeMode) => setModeState(next), [])
  const toggleTheme = React.useCallback(() => {
    setModeState((prev) => (resolveIsDark(prev) ? "light" : "dark"))
  }, [])

  const value = React.useMemo(
    () => ({ mode, setMode, isDark, toggleTheme }),
    [mode, setMode, isDark, toggleTheme],
  )

  return <ThemeContext.Provider value={value}>{children}</ThemeContext.Provider>
}

export function useTheme(): ThemeContextValue {
  const ctx = React.useContext(ThemeContext)
  if (!ctx) throw new Error("useTheme must be used within a ThemeProvider")
  return ctx
}
